import { lazy } from 'react';
import { Navigate } from 'react-router';
import type { RouteObject } from 'react-router-dom';

import Loadable from 'hoc/Loadable';

import NoPermissions from 'pages/noPermissions';
import Permissions from 'pages/permissions';

import PermissionWrapper from 'components/PermissionWrapper';

import {
    ActiveAppLinks as ActiveAppLinksPath,
    BranchesCreate,
    CampaignEdit as CampaignEditPath,
    CampaignsList as CampaignsListPath,
    CopyLink,
    CopyProductPlan,
    CreateLink,
    CreateProductPlans,
    EditLink,
    EditProductPlan,
    LinksList as LinksListPath,
} from 'constants/routerPath';

import MainLayout from 'layout/MainLayout';

const DashboardDefault = Loadable(lazy(() => import('pages/dashboard')));
const LocalizationPage = Loadable(lazy(() => import('pages/localization')));
const TemplatePage = Loadable(lazy(() => import('pages/template')));
const Projects = Loadable(lazy(() => import('pages/projects')));
const Theme = Loadable(lazy(() => import('pages/theme')));
const Screens = Loadable(lazy(() => import('pages/screens')));
const CreateScreen = Loadable(lazy(() => import('pages/screens/CreateScreenPage')));
const LTVCoefficient = Loadable(lazy(() => import('pages/ltvCoefficient')));
const AnalyticsTopics = Loadable(lazy(() => import('pages/analyticsTopics')));
const CreateBranches = Loadable(lazy(() => import('pages/branches/CreateBranchPage')));
const Branches = Loadable(lazy(() => import('pages/branches')));
const ProductCodes = Loadable(lazy(() => import('pages/productCodes')));
const ProductPlans = Loadable(lazy(() => import('pages/productPlans')));
const Campaigns = Loadable(lazy(() => import('pages/campaigns')));
const CampaignEdit = Loadable(lazy(() => import('pages/campaigns/CampaignEditPage')));
const CreateProduct = Loadable(lazy(() => import('pages/productPlans/CreateProductPage')));
const VATRate = Loadable(lazy(() => import('pages/vatRate')));
const AuditLogs = Loadable(lazy(() => import('pages/auditLogs')));
const Notifications = Loadable(lazy(() => import('pages/notifications')));
const Addons = Loadable(lazy(() => import('pages/addons')));
const GlobalTests = Loadable(lazy(() => import('pages/globalTests')));
const GlobalTestEditPage = Loadable(lazy(() => import('pages/globalTests/GlobalTestEditPage')));
const GlobalTestCreatePage = Loadable(lazy(() => import('pages/globalTests/GlobalTestCreatePage')));
const MicroTrends = Loadable(lazy(() => import('pages/microTrends')));
const LinkPage = Loadable(lazy(() => import('pages/links/LinkSetup')));
const LinksList = Loadable(lazy(() => import('pages/links')));
const ActiveAppLinksPage = Loadable(lazy(() => import('pages/links/ActiveAppLinksPage')));

const Routes: RouteObject[] = [
    {
        path: '/',
        element: <MainLayout />,
        children: [
            {
                path: '/',
                element: <DashboardDefault />,
            },
            {
                path: 'dashboard',
                children: [
                    {
                        path: 'default',
                        element: <DashboardDefault />,
                    },
                ],
            },
            {
                path: 'test',
                children: [
                    {
                        path: 'branches',
                        element: (
                            <PermissionWrapper withFallback name="branches" type="get">
                                <Branches />
                            </PermissionWrapper>
                        ),
                    },
                    {
                        path: `${BranchesCreate}/:branchId?`,
                        element: (
                            <PermissionWrapper withFallback name="branches" type="get">
                                <CreateBranches />
                            </PermissionWrapper>
                        ),
                    },
                    {
                        path: `${EditLink}/:linkId?`,
                        element: (
                            <PermissionWrapper withFallback name="links" type="get">
                                <LinkPage />
                            </PermissionWrapper>
                        ),
                    },
                    {
                        path: CreateLink,
                        element: (
                            <PermissionWrapper withFallback name="links" type="create">
                                <LinkPage />
                            </PermissionWrapper>
                        ),
                    },
                    {
                        path: `${CopyLink}/:linkId?`,
                        element: <LinkPage />,
                    },
                    {
                        path: LinksListPath,
                        element: (
                            <PermissionWrapper withFallback name="links" type="get">
                                <LinksList />
                            </PermissionWrapper>
                        ),
                    },
                    {
                        path: ActiveAppLinksPath,
                        element: (
                            <PermissionWrapper withFallback name="links" type="get">
                                <ActiveAppLinksPage />
                            </PermissionWrapper>
                        ),
                    },
                ],
            },
            {
                path: 'screen',
                children: [
                    {
                        path: 'templates',
                        element: (
                            <PermissionWrapper withFallback name="templates" type="get">
                                <TemplatePage />
                            </PermissionWrapper>
                        ),
                    },
                    {
                        path: 'screens',
                        element: (
                            <PermissionWrapper withFallback name="screens" type="get">
                                <Screens />
                            </PermissionWrapper>
                        ),
                    },
                    {
                        path: 'screens/create',
                        element: (
                            <PermissionWrapper withFallback name="screens" type="get">
                                <CreateScreen />
                            </PermissionWrapper>
                        ),
                    },
                    {
                        path: 'screens/edit/:screenId?',
                        element: (
                            <PermissionWrapper withFallback name="screens" type="get">
                                <CreateScreen />
                            </PermissionWrapper>
                        ),
                    },
                    {
                        path: 'screens/copy/:screenId?',
                        element: <CreateScreen />,
                    },
                ],
            },
            {
                path: 'content',
                children: [
                    {
                        path: 'localizations',
                        element: (
                            <PermissionWrapper withFallback name="localization" type="get">
                                <LocalizationPage />
                            </PermissionWrapper>
                        ),
                    },
                    {
                        path: 'themes',
                        element: (
                            <PermissionWrapper withFallback name="themes" type="get">
                                <Theme />
                            </PermissionWrapper>
                        ),
                    },
                ],
            },
            {
                path: 'processing',
                children: [
                    {
                        path: 'product-codes',
                        element: (
                            <PermissionWrapper withFallback name="product-codes" type="get">
                                <ProductCodes />
                            </PermissionWrapper>
                        ),
                    },
                    {
                        path: 'vat-rate',
                        element: (
                            <PermissionWrapper withFallback name="vat-rate" type="get">
                                <VATRate />
                            </PermissionWrapper>
                        ),
                    },
                    {
                        path: 'campaigns',
                        element: (
                            <PermissionWrapper withFallback name="campaign" type="get">
                                <Campaigns />
                            </PermissionWrapper>
                        ),
                    },
                    {
                        path: CampaignsListPath,
                        element: (
                            <PermissionWrapper withFallback name="campaign" type="get">
                                <Campaigns />
                            </PermissionWrapper>
                        ),
                    },
                    {
                        path: `${CampaignEditPath}/:id?`,
                        element: (
                            <PermissionWrapper withFallback name="campaign" type="get">
                                <CampaignEdit />
                            </PermissionWrapper>
                        ),
                    },
                    {
                        path: 'product-plans',
                        element: (
                            <PermissionWrapper withFallback name="products" type="get">
                                <ProductPlans />
                            </PermissionWrapper>
                        ),
                    },
                    {
                        path: CreateProductPlans,
                        element: (
                            <PermissionWrapper withFallback name="products" type="get">
                                <CreateProduct />
                            </PermissionWrapper>
                        ),
                    },
                    {
                        path: `${EditProductPlan}/:id?`,
                        element: (
                            <PermissionWrapper withFallback name="products" type="get">
                                <CreateProduct />
                            </PermissionWrapper>
                        ),
                    },
                    {
                        path: `${CopyProductPlan}/:id?`,
                        element: <CreateProduct />,
                    },
                ],
            },
            {
                path: 'analytics',
                children: [
                    {
                        path: 'ltv-coefficient',
                        element: <LTVCoefficient />,
                    },
                    {
                        path: 'analytics-topics',
                        element: <AnalyticsTopics />,
                    },
                    {
                        path: 'micro-trends',
                        element: (
                            <PermissionWrapper withFallback name="micro-trends" type="get">
                                <MicroTrends />
                            </PermissionWrapper>
                        ),
                    },
                ],
            },
            {
                path: 'admin',
                children: [
                    {
                        path: 'projects',
                        element: <Projects />,
                    },
                    {
                        path: 'users',
                        element: <Permissions />,
                    },
                    {
                        path: 'audit-log',
                        element: <AuditLogs />,
                    },
                    {
                        path: 'notifications',
                        element: (
                            <PermissionWrapper withFallback name="notifications" type="get">
                                <Notifications />
                            </PermissionWrapper>
                        ),
                    },
                    {
                        path: 'addons',
                        element: (
                            <PermissionWrapper withFallback name="addons" type="get">
                                <Addons />
                            </PermissionWrapper>
                        ),
                    },
                    {
                        path: 'global-tests',
                        element: (
                            <PermissionWrapper withFallback name="global-tests" type="get">
                                <GlobalTests />
                            </PermissionWrapper>
                        ),
                    },
                    {
                        path: 'global-tests/create',
                        element: (
                            <PermissionWrapper withFallback name="global-tests" type="create">
                                <GlobalTestCreatePage />
                            </PermissionWrapper>
                        ),
                    },
                    {
                        path: 'global-tests/edit/:globalTestId',
                        element: (
                            <PermissionWrapper withFallback name="global-tests" type="edit">
                                <GlobalTestEditPage />
                            </PermissionWrapper>
                        ),
                    },
                ],
            },
            { path: '*', element: <Navigate to="/" replace /> },
            { path: '', element: <Navigate to="/" replace /> },
        ],
    },
];

const NoPermissionRoutes: RouteObject[] = [
    {
        path: '/',
        element: <NoPermissions />,
    },
    {
        path: '/no-permissions',
        element: <NoPermissions />,
    },
    { path: '*', element: <Navigate to="/" replace /> },
    { path: '', element: <Navigate to="/" replace /> },
];

export default { Routes, NoPermissionRoutes };
